import React, { useState, useEffect } from 'react';
import { CONDOS } from '../data/mockData';
import RiskGauge from '../components/RiskGauge';
import FinancialCard from '../components/FinancialCard';
import ComplaintTimeline from '../components/ComplaintTimeline';

export default function Detail({ id, onBack }) {
  const condo = CONDOS.find(c => c.id === id);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  if (!condo) return null;

  const riskColor = condo.risk_score >= 70 ? 'var(--risk-high)' : condo.risk_score >= 40 ? 'var(--risk-medium)' : 'var(--risk-low)';
  const riskBg = condo.risk_score >= 70 ? 'var(--risk-high-bg)' : condo.risk_score >= 40 ? 'var(--risk-medium-bg)' : 'var(--risk-low-bg)';
  const riskBorder = condo.risk_score >= 70 ? 'var(--risk-high-border)' : condo.risk_score >= 40 ? 'var(--risk-medium-border)' : 'var(--risk-low-border)';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Sticky top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(10,10,15,0.9)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--border-subtle)',
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', height: 56, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={onBack} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--accent)', fontSize: 14, fontFamily: 'var(--font-body)',
            padding: '4px 0',
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{condo.name}</span>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 40, animation: mounted ? 'fadeUp 0.4s 0.05s both' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: riskBg, border: `1px solid ${riskBorder}`, borderRadius: 100,
                padding: '4px 12px', marginBottom: 12,
              }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: riskColor }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: riskColor, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  {condo.risk_label}
                </span>
              </div>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(28px, 4vw, 40px)',
                lineHeight: 1.1, letterSpacing: -0.5,
                color: 'var(--text-primary)', marginBottom: 8,
              }}>
                {condo.name}
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: 15 }}>
                {condo.address}, {condo.city}, FL {condo.zip}
              </p>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                <Meta label="Neighborhood" value={condo.neighborhood} />
                <Meta label="Units" value={condo.units} />
                <Meta label="Built" value={condo.year_built} />
                <Meta label="Managed by" value={condo.managing_entity} />
              </div>
            </div>
            <RiskGauge score={condo.risk_score} />
          </div>
        </div>

        {/* Special Assessment Alert */}
        {condo.special_assessment && (
          <div style={{
            background: 'rgba(255,69,58,0.06)',
            border: '1px solid var(--risk-high-border)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px 24px', marginBottom: 24,
            animation: mounted ? 'fadeUp 0.4s 0.1s both' : 'none',
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ fontSize: 18, marginTop: 1 }}>⚠️</div>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--risk-high)', marginBottom: 4, fontSize: 14 }}>
                  Special Assessment — {condo.special_assessment.year}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
                  {condo.special_assessment.reason}
                </div>
                <div style={{ display: 'flex', gap: 24, marginTop: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2, letterSpacing: 0.5, textTransform: 'uppercase' }}>Total Amount</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16 }}>
                      {fmt(condo.special_assessment.amount)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2, letterSpacing: 0.5, textTransform: 'uppercase' }}>Per Unit</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16 }}>
                      {fmt(condo.special_assessment.per_unit)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Health */}
        <div style={{ marginBottom: 24, animation: mounted ? 'fadeUp 0.4s 0.15s both' : 'none' }}>
          <SectionLabel>Financial Health</SectionLabel>
          <FinancialCard financials={condo.financials} />
        </div>

        {/* Complaints */}
        <div style={{ animation: mounted ? 'fadeUp 0.4s 0.2s both' : 'none' }}>
          <SectionLabel>
            Complaint History
            <span style={{
              marginLeft: 10, fontSize: 11, fontWeight: 600,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 100, padding: '2px 8px', color: 'var(--text-secondary)',
            }}>
              {condo.complaints.length}
            </span>
          </SectionLabel>
          {condo.complaints.length === 0 ? (
            <div style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '32px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No complaints on record</div>
            </div>
          ) : (
            <ComplaintTimeline complaints={condo.complaints} />
          )}
        </div>

        {/* Data Source */}
        <div style={{ marginTop: 48, padding: '20px 24px', background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', animation: mounted ? 'fadeUp 0.4s 0.25s both' : 'none' }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 8 }}>
            Data Source
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Information is derived from Florida Department of Business & Professional Regulation (DBPR) public records, including condominium project registration, financial filings, and complaint case files. Updated weekly per Chapter 119, Florida Statutes.
          </p>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 5, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}:</span>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {children}
      </span>
    </div>
  );
}

function fmt(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}
